import * as stylex from '@stylexjs/stylex'
import { memo, type ComponentPropsWithoutRef, type ElementType, type ForwardedRef, type ReactNode } from 'react'
import isEqual from 'react-fast-compare'
import { useTableContext, type TableCellVariant } from '../Table/TableContext'
import { colors } from '../tokens/colors.stylex'
import { typography } from '../tokens/typography.stylex'
import { makeExtendStyle, type ExtendStyleProps } from '../utils/makeExtendStyle/makeExtendStyle'

const _baseStyles = stylex.create({
  root: {
    padding: 16,
    color: colors.grey900,
    display: 'table-cell',
    fontSize: typography.sizeBody,
    lineHeight: typography.lineHeightBody,
    textAlign: 'left',
    verticalAlign: 'middle',
    borderBottomColor: colors.grey300,
    borderBottomStyle: 'solid',
    borderBottomWidth: 1,
  },
  head: {
    fontWeight: typography.weightSemibold,
    lineHeight: 1.5,
  },
  body: {
    color: colors.grey900,
  },
  footer: {
    color: colors.grey600,
    fontSize: typography.sizeCaption,
    lineHeight: 1.3125,
  },
  sizeSmall: {
    padding: '6px 16px',
  },
  sizeMedium: {
    padding: '16px',
  },
  paddingCheckbox: {
    padding: '0 0 0 4px',
    width: 48,
  },
  paddingNone: {
    padding: 0,
  },
  alignLeft: {
    textAlign: 'left',
  },
  alignCenter: {
    textAlign: 'center',
  },
  alignRight: {
    flexDirection: 'row-reverse',
    textAlign: 'right',
  },
  alignJustify: {
    textAlign: 'justify',
  },
  verticalAlignTop: {
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    verticalAlign: 'top',
  },
  verticalAlignMiddle: {
    alignItems: 'center',
    justifyContent: 'flex-start',
    verticalAlign: 'middle',
  },
  verticalAlignBottom: {
    alignItems: 'flex-end',
    justifyContent: 'flex-start',
    verticalAlign: 'bottom',
  },
  stickyHeader: {
    backgroundColor: colors.white,
    position: 'sticky',
    zIndex: 2,
    top: 0,
  },
})

export type TableCellAlign = 'inherit' | 'left' | 'center' | 'right' | 'justify'
export type TableCellVerticalAlign = 'inherit' | 'top' | 'middle' | 'bottom'
export type TableCellPadding = 'normal' | 'checkbox' | 'none'
export type TableCellSize = 'small' | 'medium'
export type SortDirection = 'asc' | 'desc' | false

export type TableCellProps<RootComponent extends ElementType = 'td', TableCellElement extends HTMLElement = HTMLTableCellElement> = {
  /** 自訂根元素標籤，head variant 預設為 th，其餘為 td */
  'component'?: RootComponent
  /** 儲存格內容 */
  'children'?: ReactNode
  /**
   * 水平對齊方式
   * @default 'inherit'
   */
  'align'?: TableCellAlign
  /**
   * 垂直對齊方式
   * @default 'middle'
   */
  'verticalAlign'?: TableCellVerticalAlign
  /** 內距模式，未設定時繼承 Table context */
  'padding'?: TableCellPadding
  /** th 的 scope 屬性 */
  'scope'?: string
  /** 儲存格大小，未設定時繼承 Table context */
  'size'?: TableCellSize
  /** 排序方向，用於設定 aria-sort */
  'sortDirection'?: SortDirection
  /**
   * 儲存格變體（head / body / footer），影響字體樣式
   * @default 'body'
   */
  'variant'?: TableCellVariant
  /**
   * 是否啟用固定表頭樣式
   * @default false
   */
  'stickyHeader'?: boolean
  /** 自訂根元素的 StyleX 樣式 */
  'rootStyle'?: stylex.StyleXStyles
  /** 根元素的 ref */
  'rootRef'?: ForwardedRef<TableCellElement>
  /** 測試用 data 屬性 */
  'data-testid'?: string
} & ExtendStyleProps & ComponentPropsWithoutRef<RootComponent>

const TableCellComponent = <RootComponent extends ElementType = 'td', TableCellElement extends HTMLElement = HTMLTableCellElement>(
  props: TableCellProps<RootComponent, TableCellElement>,
) => {
  const {
    component: componentProp,
    align = 'inherit',
    verticalAlign = 'middle',
    padding: paddingProp,
    scope: scopeProp,
    size: sizeProp,
    sortDirection,
    variant = 'body',
    stickyHeader = false,
    extendStyles,
    rootStyle,
    rootRef,
    ...rest
  } = makeExtendStyle(props)

  const table = useTableContext()
  const padding = paddingProp ?? table?.padding ?? 'normal'
  const size = sizeProp ?? table?.size ?? 'medium'

  // Determine variant from context if not explicitly provided
  const resolvedVariant = variant !== 'body' ? variant : (table?.variant ?? variant)

  // Determine component type
  const isHeadCell = resolvedVariant === 'head'
  const component = componentProp || (isHeadCell ? 'th' : 'td')
  const Component = component as ElementType

  // Handle scope attribute
  let scope: string | undefined = scopeProp
  if (component === 'td') {
    scope = undefined
  }
  else if (!scope && isHeadCell) {
    scope = 'col'
  }

  // Handle aria-sort
  let ariaSort: 'ascending' | 'descending' | undefined
  if (sortDirection) {
    ariaSort = sortDirection === 'asc' ? 'ascending' : 'descending'
  }

  const componentProps: Record<string, unknown> = {
    'ref': rootRef,
    'aria-sort': ariaSort,
  }

  if (scope) {
    componentProps.scope = scope
  }

  return (
    <Component
      {...componentProps}
      {...stylex.props(
        _baseStyles.root,
        resolvedVariant === 'head' && _baseStyles.head,
        resolvedVariant === 'body' && _baseStyles.body,
        resolvedVariant === 'footer' && _baseStyles.footer,
        size === 'small' && _baseStyles.sizeSmall,
        size === 'medium' && _baseStyles.sizeMedium,
        padding === 'checkbox' && _baseStyles.paddingCheckbox,
        padding === 'none' && _baseStyles.paddingNone,
        align === 'left' && _baseStyles.alignLeft,
        align === 'center' && _baseStyles.alignCenter,
        align === 'right' && _baseStyles.alignRight,
        align === 'justify' && _baseStyles.alignJustify,
        verticalAlign === 'top' && _baseStyles.verticalAlignTop,
        verticalAlign === 'middle' && _baseStyles.verticalAlignMiddle,
        verticalAlign === 'bottom' && _baseStyles.verticalAlignBottom,
        stickyHeader && _baseStyles.stickyHeader,
        rootStyle,
        extendStyles,
      )}
      {...rest}
    />
  )
}

/**
 * TableCell 是表格儲存格元件，支援對齊、內距、排序與固定表頭等功能。
 * 會自動從 TableContext 繼承 padding、size 與 variant 設定。
 */
export const TableCell = memo(TableCellComponent, isEqual) as typeof TableCellComponent
